import {
  get,
  set,
  setImmutable as setFp,
  merge,
  uniq,
  assign,
  keys,
  isNil,
  reduce,
} from "@/utils/object";

import { BookingFormManager } from "../FormManager/BookingFormManager";
import { normalizeDate } from "@/utils/datetime";
import type {
  BookingFormInterface,
  BookingSlotInterface,
  Field,
} from "../FormManager/types";
import { CLASS_CONSTANTS } from "@/utils/constants";
import type { FormObject } from "./FormFields/FormFieldModel";
import { translate } from "@/utils/translationService";
import type {
  ControlNodeType,
  LayoutSchema,
} from "@binaryoperations/json-schema-form/core/models";
import type { NumberString } from "@/store/api/dto/options";
import type { FlashMessageDTO } from "@/store/api/dto/response.dto";

export const BOOK_NOW_EVENT = "ws-book-now-event";

export const ADMIN_FORM_TOGGLES = [
  "is_validation_unstrict",
  "disable_customer_personal_data",
  "not_send_bills",
  "without_billing",
  "not_send_bookings",
  "draft_bills",
  "draft_bookings",
  "waitlist_bookings",
];

export const ENABLED_ADMIN_FORM_TOGGLES_MAP = {};

export const normalizeDefaultFormData = (
  formData: Partial<BookingFormInterface> = {},
) => {
  const consentAgreementPath = "form_data.consent_agreement";
  const formFieldPath = "form_data.form_field";

  const consentAgreement = get(formData, consentAgreementPath);
  const formField = get(formData, formFieldPath);

  if (Array.isArray(consentAgreement)) {
    formData = setFp(
      consentAgreementPath,
      merge({}, consentAgreement),
      formData,
    );
  }

  if (!formField) {
    formData = setFp(formFieldPath, [], formData);
  }

  formData = setFp(
    formFieldPath,
    get(formData, formFieldPath, []).map((x) => ({
      ...reduce(
        x,
        (participantData: Record<string, unknown>, value, key) => {
          if (isNil(value)) return participantData;

          participantData[key] = value;

          if (value && typeof value === "object" && "date" in value) {
            participantData[key] = value.date;
          }

          return participantData;
        },
        {},
      ),
      $$meta: {
        $$isSavedParticipant: true,
      },
    })),
    formData,
  );

  return formData;
};

export const normalizeDefaultBillReceiverIndex = <T extends object>(
  formData: T,
) => {
  const billReceiverIndex = get(formData, "booking_data.bill_receiver_index");
  if (typeof billReceiverIndex === "number") {
    return setFp(
      "booking_data.bill_receiver_index",
      "" + billReceiverIndex,
      formData,
    );
  }

  return formData;
};

export const onCategoryChange = (index: number) => {
  return (state: object) => ({
    categoryNames: get(state, "categoryNames", [])?.slice(0, index + 1),
  });
};

export const getParticipantTitle = (
  translateKey: string,
  participant: object,
) => {
  const props = merge(
    { title: "", first_name: "", last_name: "", company: "", salutation: "" },
    participant,
  );
  return (translate(translateKey, props) || "").trim() || "";
};

type Conditional = {
  and?: (string | Conditional)[];
  or?: (string | Conditional)[];
};

const participantConditions = {
  or: [{ and: ["company"] }, { or: ["first_name", "last_name"] }],
};
const concludeOrEvaluate = (
  data: unknown,
  condition: string | Partial<Conditional>,
) => {
  return typeof condition === "string"
    ? !!get(data, condition)
    : concludeConditions(data, condition);
};

const concludeConditions = (
  data: unknown,
  { and = [], or = [] }: Partial<Conditional> = {},
): boolean => {
  return (
    (or.length === 0 && and.length === 0) ||
    (or.length > 0 && or.some(concludeOrEvaluate.bind(null, data))) ||
    (and.length > 0 && and.every(concludeOrEvaluate.bind(null, data)))
  );
};

export const isParticipantValid = (participant: unknown) =>
  concludeConditions(participant, participantConditions);

export const getParticipantsAsSelectOptions = (
  participants: Field[],
  getParticipantConfig: {
    createNewParticipant?: boolean | NumberString;
    selection?: NumberString;
    excludedParticipantsIndices?: number[];
    salutations?: Record<string, { label: string }>;
    allParticipantCount?: number;
    participantLabel?: string;
    participantHeader?: string;
    newParticipantLabel?: string;
    defaultIndex?: NumberString;
  } = {},
) => {
  const {
    salutations = {},
    createNewParticipant = false,
    selection,
    defaultIndex,
    excludedParticipantsIndices = [],
    allParticipantCount = participants.length,
    participantLabel = "bookcart.participant_name",
    participantHeader = "bookcart.participant_header",
    newParticipantLabel = "bookcart.add_new_participant",
  } = getParticipantConfig;

  const allParticipants = participants
    .filter(Boolean)
    .map((participant, i) => {
      const index = +(participant.$$meta?.$$index ?? i);
      if (excludedParticipantsIndices.includes(index)) return null;

      if (
        !participant.$$meta?.$$dirty &&
        (!selection || +selection !== i) &&
        (!defaultIndex || +defaultIndex !== index)
      ) {
        return null;
      }

      let label = "";
      if (isParticipantValid(participant)) {
        label = getParticipantTitle(participantLabel, {
          ...participant,
          salutation: salutations[participant?.salutation_id as string]?.label,
        });
      }

      if (!label.trim()) {
        label = translate(participantHeader, {
          index: index + 1,
        });
      }

      return {
        value: "" + index,
        label,
        disabled: false,
      };
    })
    .filter((x) => x !== null);

  if (createNewParticipant === selection) {
    return allParticipants;
  }

  if (createNewParticipant) {
    const nextIndex =
      typeof createNewParticipant === "boolean"
        ? allParticipantCount + ""
        : createNewParticipant;

    allParticipants.push({
      value: nextIndex,
      disabled: !participants[selection!]?.$$meta?.$$dirty,
      label: translate(newParticipantLabel),
    });
  }

  return allParticipants;
};

type NodeSchema = LayoutSchema<object, { error_message?: string }>;
export const getFieldPaths = (
  arr: NodeSchema[],
  mergedProps: { invalidStepIndex: null | number } = { invalidStepIndex: null },
  parentIndex: number | null = null,
) => {
  return arr
    .filter((x) => x !== undefined)
    .reduce((allNames: string[], field, index): string[] => {
      const stepIndex = parentIndex === null ? index : parentIndex;

      if (field.type !== "control") {
        return allNames.concat(
          getFieldPaths(field.nodes ?? [], mergedProps, stepIndex),
        );
      }

      const control = field as ControlNodeType<
        object,
        { error_message?: string }
      >;

      if (
        control.options?.error_message &&
        mergedProps.invalidStepIndex === null
      ) {
        mergedProps.invalidStepIndex = stepIndex;
      }

      if (control.path) allNames.push(control.path);
      return allNames;
    }, []);
};

export const getFilteredData = (
  fieldPaths: string[],
  form: object,
  switchablePathConstant = "",
) => {
  return fieldPaths.reduce(
    (filteredValues, path) => {
      if (switchablePathConstant && path.startsWith(switchablePathConstant))
        return filteredValues;

      const actualPath = path.match(/(\.date)$/)
        ? path.replace(/(\.date)$/, "")
        : path;
      const value = get(form, actualPath);
      if (value || ![null, undefined].includes(value)) {
        set(filteredValues, actualPath, value);
      }
      return filteredValues;
    },
    {
      form_data: {
        consent_agreement: {},
        form_group: {},
        form_field: {},
      },
    } as object,
  );
};

export const getParticipantIndex = (
  indices: (string | null | undefined)[] = [],
  index: number,
  startIndex = 0,
): string => {
  const uniqIndices = uniq(indices);
  const participantIndex = get(uniqIndices, index);
  if ([undefined, null, ""].includes(participantIndex)) {
    for (let counter = startIndex; ; counter++) {
      if (!indices.some((i) => counter === Number(i))) {
        indices.push("" + counter);
        return "" + counter;
      }
    }
  }

  return "" + participantIndex;
};

export const normalizeBookingSlot = (bookingSlot: {
  from?: { date: string } | string;
  to?: { date: string } | string;
}) => {
  if (!bookingSlot) return bookingSlot;
  const from =
    typeof bookingSlot.from === "string"
      ? { date: bookingSlot.from }
      : (bookingSlot.from ?? { date: undefined });

  const to =
    typeof bookingSlot.to === "string"
      ? { date: bookingSlot.to }
      : (bookingSlot.to ?? { date: undefined });

  return assign(bookingSlot, {
    from: normalizeDate(from.date!),
    to: normalizeDate(to.date!),
  });
};

export const getBookingSlot = (form: object) => {
  return get(form, "booking_data.booking_slot") as
    | BookingSlotInterface
    | undefined;
};

export const CART_ITEMS_PATH = "booking_data.cart_items";
export const getTimespanId = (form: object) => {
  return get(form, TIMESPAN_ID_PATH);
};

export const getPriceId = (form: object) => {
  return get(form, PRICE_ID_PATH);
};

export const getBookersCount = (form: object) => {
  return get(form, BOOKER_COUNT_PATH);
};

export const getExtraPrices = (form: object) => {
  return (get(form, CART_ITEMS_PATH) || []).slice(1);
};

export const getPersonalDataIndex = (form: object) => {
  return get(form, PERSONAL_DATA_INDEX_PATH);
};

export const getCartItemPath = (index: number) => {
  return CART_ITEMS_PATH + `.${index}`;
};

export const getCartItemPriceIdPath = (index: number) => {
  return CART_ITEMS_PATH + `.${index}.position.price_id`;
};
export const getCartItemQuantityPath = (index: number) => {
  return CART_ITEMS_PATH + `.${index}.position.quantity`;
};
export const getCartItemTimespanIdPath = (index: number) => {
  return CART_ITEMS_PATH + `.${index}.booking.course_timespan_id`;
};
export const getCartItemPersonalDataPathIndex = (index: number) => {
  return CART_ITEMS_PATH + `.${index}.booking.personal_data_index`;
};

export const buildPath = (path: string) => path.replace(/\./g, "/");

export const ROOT_CART_ITEM_INDEX = 0;
export const TIMESPAN_ID_PATH = getCartItemTimespanIdPath(ROOT_CART_ITEM_INDEX);
export const PRICE_ID_PATH = getCartItemPriceIdPath(ROOT_CART_ITEM_INDEX);
export const BOOKER_COUNT_PATH = getCartItemQuantityPath(ROOT_CART_ITEM_INDEX);
export const PERSONAL_DATA_INDEX_PATH =
  getCartItemPersonalDataPathIndex(ROOT_CART_ITEM_INDEX);
export const COMMENTS_PATH = `form_data.form_field.course_participants[${ROOT_CART_ITEM_INDEX}].comment`;

export const getReferenceFieldPath = (formGroupId: string) =>
  `field_ref.reference_${formGroupId}`;

export const ADMIN_SETTINGS_KEY = "admin_config_toggles";
export const getAdminSettings = (form: object) => {
  return get(form, ADMIN_SETTINGS_KEY);
};

export const deriveFieldMessage = (
  form: FormObject,
  validationErrors: Record<string, string[]>,
  fieldName: string,
) => {
  const billReceiverIndex = form?.billRecipientIndex;
  const errorFieldName = fieldName.replace("form_data.", "");
  const billErrorFieldName = errorFieldName.replace(
    /personal_data\.\d+/,
    `bill.${billReceiverIndex}`,
  );
  return (validationErrors[billErrorFieldName] ||
    validationErrors[errorFieldName] ||
    [])[0];
};

export const deriveValidationMessages = (
  formManager: BookingFormManager,
  validationErrors: Record<string, string[]>,
  fieldPaths: string[],
  steps: LayoutSchema[] = [],
): FlashMessageDTO[] => {
  if (!formManager) return [];

  if (!validationErrors) return [];

  const form = formManager.toRequestBody(fieldPaths);
  const participants = get(form, "form_data.form_field");
  const billReceiverIndex = "" + get(form, "booking_data.bill_receiver_index");
  const map: Record<string, boolean> = {};
  const personalDataIndices = (form?.booking_data?.cart_items ?? []).reduce(
    (
      cartIndices: {
        messages: string[];
        indices: string[];
        billerStep: boolean;
        index?: number;
      }[],
      cartItem,
    ) => {
      const parentPriceId = get(cartItem, "position.parent_price_id", null);
      if (parentPriceId !== null) {
        return cartIndices;
      }

      if (parentPriceId === null) {
        const priceId =
          get(cartItem, "position.price_id") &&
          `priceId_${get(cartItem, "position.price_id")}`;
        const timespanId =
          get(cartItem, "booking.course_timespan_id") &&
          `timespanId_${get(cartItem, "booking.course_timespan_id")}`;
        const id = (priceId || timespanId)!;
        if (!map[id]) {
          map[id] = true;
          cartIndices.push({
            messages: [],
            indices: [],
            billerStep: false,
            index: cartIndices.length,
          });
        }
      }

      cartIndices
        .slice(-1)[0]
        .indices.push("" + get(cartItem, "booking.personal_data_index"));

      return cartIndices;
    },
    [],
  );

  personalDataIndices.push({
    messages: [],
    indices: [billReceiverIndex],
    billerStep: true,
  });

  const cartIndices: Record<string, number> = {};
  const extractPositionRegex = new RegExp("form_field\\.(?<position>\\d+)\\.");

  keys(validationErrors).forEach((key) => {
    const position =
      key.match(new RegExp(extractPositionRegex))?.groups?.position || "";

    if (!position) {
      return;
    }

    if (cartIndices[position] === undefined) {
      for (
        let cartIndex = 0;
        cartIndex < personalDataIndices.length;
        cartIndex++
      ) {
        const dataIndices = personalDataIndices[cartIndex];
        if (dataIndices.indices.includes("" + position)) {
          cartIndices[position] = cartIndex;
          break;
        }
      }
    }

    if (cartIndices[position] === undefined) {
      return;
    }

    const cartIndex = cartIndices[position];
    const dataIndices = personalDataIndices[cartIndex];
    const isBillerStep = dataIndices.billerStep;

    const participantName =
      billReceiverIndex === position
        ? "bookcart.biller_participant_name"
        : "bookcart.participant_name";
    const participantHeader =
      billReceiverIndex === position
        ? "bookcart.biller_participant_header"
        : "bookcart.participant_header";
    const participant = participants?.[+position] || {};
    const label =
      (isParticipantValid(participant) &&
        getParticipantTitle(participantName, participant)) ||
      translate(participantHeader, { index: +position + 1 });

    const prefix = isBillerStep ? "" : `${label}: `;
    dataIndices.messages.push(
      `<li class="${CLASS_CONSTANTS.NOTIFICATION.VALIDATION_MESSAGE.ROOT}">${prefix} ${deriveFieldMessage(form as FormObject, validationErrors, key)}</li>`,
    );
  }, [] as string[]);

  const message = personalDataIndices.reduce(
    (message, { messages, billerStep }, index) => {
      if (messages.length === 0) return message;

      const header = billerStep
        ? translate("bookcart.bill_recipient")
        : (steps[index]?.options?.title as string);

      return (
        message +
        `<h4 class="${CLASS_CONSTANTS.NOTIFICATION.VALIDATION_MESSAGE.HEADER}">${header}</h4><ul class="${CLASS_CONSTANTS.NOTIFICATION.VALIDATION_MESSAGE.LIST}"> ${messages.join("\n")} </ul>`
      );
    },
    "",
  );

  return message.length <= 0
    ? []
    : [
        {
          level: "danger",
          message,
          aged: false,
        },
      ];
};

export const getFirstTimespanId = (state: object, formName: string) => {
  try {
    const form = get(state, `formReducer.forms.${formName}`, {}),
      timespanIds = get(form, "booking_data.cart_items", [])
        .map((cartItem) => get(cartItem, "booking.course_timespan_id", null))
        .filter(Boolean)
        .slice(0, 1);

    return timespanIds.map((x) => String(x))[0] || "";
  } catch {
    return [];
  }
};
